import {
  KIDS_DEPARTMENT_URL_KEY,
  MENS_DEPARTMENT_URL_KEY,
  WOMENS_DEPARTMENT_URL_KEY,
} from '@/modules/categories/constants';
import { type CMS_LOCALES } from '@/modules/cms/constants';
import { type Brand, type CMSBrandCategoryPage } from '@/modules/cms/types';
import { capitalise } from '@/modules/text/helpers';

/**
 *
 * @param pageMetadata - The page metadata object from the CMS
 * @param categories - Array of slugs e.g. "/mens/tops/cardigans" is represented as ['mens', 'tops', 'cardigans']
 * @param brand - The brand
 * @param includeBrand - Whether to include the brand in the title
 * @param locale - The website locale
 */
export function getPageTitle({
  pageMetadata,
  categories,
  brand,
  includeBrand = false,
  locale,
}: {
  pageMetadata?: Partial<CMSBrandCategoryPage>;
  categories: string[];
  brand?: Brand;
  includeBrand?: boolean;
  locale: CMS_LOCALES;
}) {
  if (pageMetadata?.title) {
    return pageMetadata?.title;
  }

  const [departmentOrGroup] = categories;
  const isEverythingElseDepartment = ![
    MENS_DEPARTMENT_URL_KEY,
    WOMENS_DEPARTMENT_URL_KEY,
    KIDS_DEPARTMENT_URL_KEY,
  ].includes(categories[0]);

  /**
   * The Everything Else department only has one category in the URL e.g. "/face-masks" or "/art",
   * as opposed to other departments which have which has multiple categories e.g. "/mens/tops/cardigans".
   *
   * In this scenario, we want to return the group name in a capitalised format.
   */
  if (categories.length === 1 && isEverythingElseDepartment) {
    return capitalise(departmentOrGroup.replaceAll('-', ' '));
  }

  /**
   * Due to the case above, we can now assume that the first category in the URL is a department e.g. "mens"
   * For "everything-else" department, we don't include the department in the title
   */
  if (isEverythingElseDepartment) {
    const activeCategoryName = categories[categories.length - 1].replaceAll(
      '-',
      ' '
    );
    return capitalise(activeCategoryName);
  }

  const department = locale.includes('en')
    ? `${capitalise(departmentOrGroup)} `
    : '';
  let departmentInPossessiveForm = '';
  if (locale.includes('en')) {
    if (departmentOrGroup === KIDS_DEPARTMENT_URL_KEY) {
      departmentInPossessiveForm =
        department.slice(0, department.length - 1) + `' `;
    } else {
      departmentInPossessiveForm =
        department.slice(0, department.length - 2) + `'s `;
    }
  }

  const activeCategoryName = categories[categories.length - 1].replaceAll(
    '-',
    ' '
  );

  if (includeBrand && brand?.name) {
    return `${department}${capitalise(brand.name)} ${capitalise(
      activeCategoryName
    )}`;
  }

  return departmentInPossessiveForm + capitalise(activeCategoryName);
}
